//////////////////////////
//// VARIANT CHANGES ////
////////////////////////

class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this.variantImageData = JSON.parse(
      document.getElementById("variant-image-data").textContent
    );
    this.variantData = JSON.parse(
      document.getElementById("variant-data").textContent
    );
    const parentElement = document.getElementById("product--widget");
    this.enableVariantImages =
      parentElement.dataset.enableVariantImages === "variant_img_true";
    this.options = [];
    this.currentVariant = null;
    this.initialLoad = true; // Flag to handle the initial load
    this.debouncedOnVariantChange = this.debounce(
      this.onVariantChange.bind(this),
      300
    );

    this.addEventListener("change", this.debouncedOnVariantChange);
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.initialLoad = false;

      // Set initial variant immediately
      this.options = this.getCurrentOptions();
      this.setInitialVariant();

      // Populate initial images if variant images are enabled
      if (this.enableVariantImages) {
        this.updateImages();
      }
    });
  }

  setInitialVariant() {
    const initialVariant = this.variantData.find((variant) =>
      variant.options.every((option, index) => this.options[index] === option)
    );

    if (initialVariant) {
      this.currentVariant = initialVariant;
      this.updateFormID();
      this.updateURL();
    }
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  getCurrentOptions() {
    return Array.from(
      this.querySelectorAll('select, input[type="radio"]:checked'),
      (element) => element.value
    );
  }

  onVariantChange() {
    if (this.initialLoad) return; // Skip processing during initial load

    const newOptions = this.getCurrentOptions();
    if (JSON.stringify(newOptions) === JSON.stringify(this.options)) return; // Skip if options haven't changed

    const firstOptionChanged = newOptions[0] !== this.options[0];
    this.options = newOptions;

    const newVariant = this.variantData.find((variant) =>
      variant.options.every((option, index) => this.options[index] === option)
    );

    if (
      newVariant &&
      newVariant.id !== (this.currentVariant && this.currentVariant.id)
    ) {
      this.currentVariant = newVariant;
      this.updateURL();
      this.updateFormID();
      this.updatePriceAndButton();
      if (firstOptionChanged && this.enableVariantImages) this.updateImages();
    }
  }

  updateURL() {
    if (this.currentVariant) {
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.currentVariant.id}`
      );
    }
  }

  updateFormID() {
    const formInput = document.querySelector("#product-form input[name='id']");
    if (formInput) {
      formInput.value = this.currentVariant.id;
    }
  }

  updatePriceAndButton() {
    const url = `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`;
    fetch(url)
      .then((response) => response.text())
      .then((htmlResponse) => {
        const doc = new DOMParser().parseFromString(htmlResponse, "text/html");
        this.updatePrice(doc);
        this.updateButton(doc);
      })
      .catch((error) =>
        console.error("Error updating price and button:", error)
      );
  }

  updatePrice(doc) {
    const newPrice = doc.querySelector(`#price-${this.dataset.section}`);
    const currentPrice = document.getElementById(
      `price-${this.dataset.section}`
    );
    if (
      currentPrice &&
      newPrice &&
      currentPrice.innerHTML !== newPrice.innerHTML
    ) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }
  }

  updateButton(doc) {
    const updatedButton = doc.querySelector("#add-to-cart");
    const inventoryQuantity = parseInt(updatedButton.dataset.quantity, 10);
    const addButton = document.getElementById("add-to-cart");

    if (!addButton || !this.currentVariant) {
      console.error("addButton or currentVariant is not defined");
      return;
    }

    const buttonText = !this.currentVariant.available
      ? addButton.dataset.notavailable
      : inventoryQuantity <= 0
      ? addButton.dataset.preorder
      : addButton.dataset.addcart;

    const buttonColor = !this.currentVariant.available
      ? "var(--unavailable-grey)"
      : inventoryQuantity <= 0
      ? "var(--aubergine)"
      : "var(--dark-grey)";

    if (
      addButton.textContent !== buttonText ||
      addButton.style.backgroundColor !== buttonColor
    ) {
      addButton.textContent = buttonText;
      addButton.style.backgroundColor = buttonColor;
      addButton.disabled = !this.currentVariant.available;
    }
  }

  updateImages() {
    const firstOption =
      this.options.length > 0
        ? this.options[0]
        : Object.keys(this.variantImageData)[0];
    const variantImages = this.variantImageData.find(
      (image) => image.name === firstOption
    );

    if (variantImages) {
      const thumbnailSlider = document.getElementById("thumbnail-slider");
      const mainImageSlider = document.getElementById("main-image-slider");

      const generateThumbnailHTML = (imagesSmall) =>
        Object.keys(imagesSmall)
          .map((key) =>
            imagesSmall[key]
              ? `<div class="swiper-slide">
                  <img class="product--media" src="${imagesSmall[key]}" alt="Thumbnail Slide Image ${key}">
                </div>`
              : ""
          )
          .join("");

      const generateMainImageHTML = (images, altTexts) =>
        Object.keys(images)
          .map(
            (key, index) =>
              images[key] &&
              `<div class="swiper-slide">
                <div class="product-image-wrapper">
                  <div class="swiper-zoom-container">
                    <img class="product--media" src="${images[key]}" alt="${
                      altTexts[key]
                    }" loading="${index === 0 ? "eager" : "lazy"}" fetchpriority="${
                      index === 0 ? "high" : "low"
                    }">
                  </div>
                </div>
              </div>`
          )
          .join("");

      const thumbnailHTML = generateThumbnailHTML(variantImages.images_small);
      const mainImageHTML = generateMainImageHTML(
        variantImages.images,
        variantImages.alt_text
      );

      thumbnailSlider.querySelector(".swiper-wrapper").innerHTML =
        thumbnailHTML;
      mainImageSlider.querySelector(".swiper-wrapper").innerHTML =
        mainImageHTML;

      document
        .querySelectorAll('link[rel="preload"]')
        .forEach((link) => link.remove());

      if (variantImages.images.image_1) {
        const link1 = document.createElement("link");
        link1.rel = "preload";
        link1.as = "image";
        link1.href = variantImages.images.image_1;
        document.head.appendChild(link1);
      }

      document.dispatchEvent(new Event("imagesUpdated"));
    }
  }
}

customElements.define("variant-selector", VariantSelector);

///////////////////////////////////
/////// PRODUCT WIDGET  //////////
/////////////////////////////////

function initializeSwipers() {
  if (!window.thumbSwiper) {
    window.thumbSwiper = new Swiper("#thumbnail-slider", {
      slidesPerView: "auto",
      direction: "vertical",
      touchStartPreventDefault: false,
      onlyExternal: true,
      threshold: 10,
    });
  }

  if (!window.mainSwiper) {
    window.mainSwiper = new Swiper("#main-image-slider", {
      loop: false,
      navigation: {
        nextEl: "#main-image-slider .swiper-button-next",
        prevEl: "#main-image-slider .swiper-button-prev",
      },
      thumbs: { swiper: window.thumbSwiper },
      pagination: {
        el: "#main-image-slider .swiper-pagination",
        clickable: true,
      },
      // Only add zoom if screen width is less than 800px
      zoom: window.innerWidth < 800 ? { maxRatio: 1.8 } : false,
    });
  }
}


document.addEventListener("DOMContentLoaded", () => {
  initializeSwipers();

  document.addEventListener("imagesUpdated", () => {
    if (!window.mainSwiper || !window.thumbSwiper) initializeSwipers();
    else {
      window.mainSwiper.update();
      window.thumbSwiper.update();
    }

    // Reattach hover listeners when images are updated
    attachImageHoverListeners();
  });


  // Image hovering effect

  function attachImageHoverListeners() {
    document
      .querySelectorAll("#main-image-slider .product-image-wrapper")
      .forEach((wrapper) => {
        wrapper.addEventListener("mousemove", (e) => {
          const rect = e.target.getBoundingClientRect();
          const x = e.clientX - rect.left; // x position within the element.
          const y = e.clientY - rect.top; // y position within the element.
          const originX = (x / rect.width) * 100;
          const originY = (y / rect.height) * 100;
          e.target.style.transformOrigin = `${originX}% ${originY}%`;
        });

        wrapper.addEventListener("mouseleave", (e) => {
          e.target.style.transformOrigin = "center center";
        });
      });

    const pagination = document.querySelector(
      "#main-image-slider .swiper-pagination"
    );

    document.querySelectorAll(".product-image-wrapper").forEach((wrapper) => {
      wrapper.addEventListener("mouseenter", () =>
        pagination.classList.add("product--pagination-hovered")
      );
      wrapper.addEventListener("mouseleave", () =>
        pagination.classList.remove("product--pagination-hovered")
      );
    });

    console.log("✅ Hover listeners attached");
  }

   // NOTE: use this lazyloading method for all lazyloading images, but with intersection observer?
  // FIXME: should I replace this code with the regular lazyload class?

  // Image quality swap logic
  const loadImages = () => {
    if (window.innerWidth >= 800) {
      document
        .querySelectorAll("#thumbnail-slider .swiper-slide img")
        .forEach((img) => {
          if (
            img.getAttribute("data-src") &&
            img.src !== img.getAttribute("data-src")
          ) {
            img.setAttribute("src", img.getAttribute("data-src"));
            img.removeAttribute("data-src");
          }
        });
    }
  };

  loadImages(); // Load images on initial load
  window.addEventListener("resize", loadImages);

  // Initial hover listeners setup
  attachImageHoverListeners();
});

/////////////////////////////
/////// OTHER CODE /////////
///////////////////////////

// Readmore collapsing animation toggle of the truncated product description

document.addEventListener("DOMContentLoaded", function () {
  const descriptionContainer = document.querySelector(".product--description");
  const firstElement = descriptionContainer.querySelector("p:first-of-type");
  const readMoreBtn = document.querySelector(".read-more-btn");

  if (!descriptionContainer || !firstElement || !readMoreBtn) return;

  // Ensure styles are fully applied before measuring height
  requestAnimationFrame(() => {
    const initialHeight = firstElement.getBoundingClientRect().height + 1;
    console.log("Initial Height of First <p>:", initialHeight); // Debugging
    console.log(
      "Computed margin-bottom:",
      window.getComputedStyle(firstElement).marginBottom
    );
    console.log(
      "Computed line-height:",
      window.getComputedStyle(firstElement).lineHeight
    );
    console.log("First paragraph content:", firstElement.innerHTML);

    descriptionContainer.style.height = `${initialHeight}px`;

    const fullHeight = descriptionContainer.scrollHeight + 1;
    console.log("Full Height:", fullHeight); // Debugging

    readMoreBtn.addEventListener("click", function () {
      const isExpanded = readMoreBtn.getAttribute("aria-expanded") === "true";
      descriptionContainer.style.height = isExpanded
        ? `${initialHeight}px`
        : `${fullHeight}px`;
      readMoreBtn.setAttribute("aria-expanded", !isExpanded);
      readMoreBtn.textContent = !isExpanded
        ? readMoreBtn.getAttribute("data-read-less")
        : readMoreBtn.getAttribute("data-read-more");
    });
  });
});

// information accordion toggles

document.addEventListener("DOMContentLoaded", () => {
  // Descriptions at the end of the product from - Accordion Toggles
  const items = document.querySelectorAll(".product--accordion__btn");

  const toggleAccordion = function () {
    const itemToggle = this.getAttribute("aria-expanded");

    items.forEach((item) => {
      item.setAttribute("aria-expanded", "false");
      item.nextElementSibling.style.maxHeight = 0;
      item.nextElementSibling.style.opacity = 0;
    });

    if (itemToggle == "false") {
      this.setAttribute("aria-expanded", "true");
      this.nextElementSibling.style.maxHeight = `${this.nextElementSibling.scrollHeight}px`;
      this.nextElementSibling.style.opacity = 1;
    }
  };

  items.forEach((item) => item.addEventListener("click", toggleAccordion));
});

//////////////////////////
//// ENGRAVING MODAL ////
//////////////////////////

document.addEventListener("DOMContentLoaded", () => {
  const modal = document.getElementById("engravingModal");
  const overlay = document.getElementById("engravingOverlay");
  const body = document.body;
  const customDescription = document.getElementById("custom_description");
  const charCount = document.getElementById("charCount");
  const engravingButton = document.getElementById("engravingButton");
  const submitButton = document.querySelector(".engraving-btn-submit");
  const deleteButton = document.querySelector(".engraving-btn-delete");
  const closeButton = document.querySelector(".engraving-btn-close");

  let previousEngravingText = "";
  let scrollPosition = 0;

  const getEngravingText = (attribute) =>
    document.querySelector(".product--engraving").getAttribute(attribute);

  const preventScroll = (e) => e.preventDefault();

  const hideModalAndOverlay = () => {
    modal.style.right = "-100%";
    setTimeout(() => {
      modal.style.visibility = "hidden";
    }, 300);
    overlay.style.display = "none";
    body.classList.remove("no-scroll");
    window.removeEventListener("touchmove", preventScroll, { passive: false });
    window.scrollTo(0, scrollPosition);
    body.style.top = "";
    modal.setAttribute("aria-hidden", "true");
    engravingButton.setAttribute("aria-expanded", "false");
  };

  const updateButtonText = (newText, iconHTML = "") => {
    engravingButton.innerHTML = `${newText}${iconHTML}`;
  };

  const updateCharacterCount = () => {
    const maxCharacters = parseInt(getEngravingText("data-max-characters"));
    const remaining = maxCharacters - customDescription.value.length;
    const charactersRemainingText = getEngravingText(
      "data-characters-remaining-text"
    );
    charCount.textContent = `${remaining} ${charactersRemainingText}`;
    charCount.style.color = remaining === 0 ? "darkred" : "";
  };

  const toggleEngraving = () => {
    if (modal.style.right === "0px") {
      hideModalAndOverlay();
    } else {
      scrollPosition = window.pageYOffset;
      body.style.top = `-${scrollPosition}px`;
      modal.style.right = "0px";
      modal.style.visibility = "visible";
      overlay.style.display = "block";
      body.classList.add("no-scroll");
      window.addEventListener("touchmove", preventScroll, { passive: false });
      previousEngravingText = customDescription.value.trim();
      updateCharacterCount();
      modal.setAttribute("aria-hidden", "false");
      engravingButton.setAttribute("aria-expanded", "true");
      customDescription.focus();
    }
  };

  const submitEngraving = () => {
    hideModalAndOverlay();
    const engravingText = customDescription.value.trim();
    const modifyEngravingText = getEngravingText("data-modify-engraving-text");
    const addEngravingText = getEngravingText("data-add-engraving-text");
    updateButtonText(
      engravingText ? modifyEngravingText : addEngravingText,
      engravingText
        ? `<svg class="icon-arrow-right" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
           <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
         </svg>`
        : `<svg class="icon-plus" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
           <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />`
    );
    previousEngravingText = engravingText;
    updateCharacterCount();
  };

  const deleteEngraving = () => {
    customDescription.value = "";
    hideModalAndOverlay();
    const addEngravingText = getEngravingText("data-add-engraving-text");
    updateButtonText(
      addEngravingText,
      `<svg class="icon-plus" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
       <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />`
    );
    updateCharacterCount();
  };

  const closeEngravingModal = () => {
    const currentEngravingText = customDescription.value.trim();
    if (previousEngravingText) {
      customDescription.value = previousEngravingText;
      updateCharacterCount();
    } else if (!currentEngravingText) {
      deleteEngraving();
    } else {
      customDescription.value = "";
      updateCharacterCount();
    }
    hideModalAndOverlay();
    engravingButton.focus();
  };

  const trapFocus = (event) => {
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    }
  };

  const handleKeydown = (event) => {
    if (event.key === "Escape") {
      closeEngravingModal();
    } else if (
      event.key === "Tab" &&
      modal.getAttribute("aria-hidden") === "false"
    ) {
      trapFocus(event);
    }
  };

  // Attach event listeners
  engravingButton.addEventListener("click", toggleEngraving);
  submitButton.addEventListener("click", submitEngraving);
  deleteButton.addEventListener("click", deleteEngraving);
  closeButton.addEventListener("click", closeEngravingModal);
  overlay.addEventListener("click", closeEngravingModal);
  customDescription.addEventListener("input", updateCharacterCount);
  document.addEventListener("keydown", handleKeydown);
});
